import { AxiosInstance } from 'axios';
import { createContextAwareHandlers } from './instance';

interface PersonDetails {
  category?: string;
  countryCode?: string;
  countryName?: string;
  plan?: string;
  days?: number;
  months?: number;
  years?: number;
  personCount?: number;
  type?: string;
}
interface EventDetails {
  countryCode?: string;
  countryName?: string;
  plan?: string;
  days?: number;
  personCount?: number;
  participantType?: string;
}

interface ShippingDetails {
  activity?: string;
  type?: string;
  size?: string;
  weight?: number;
  lca?: string;
  distanceKm?: number;
}
export interface FlightDetails {
  travelClass?: TravelClass;
  flights?: number;
  passengers?: number;
  roundTrip?: boolean;
  routeLegs?: RouteLegs[];
}
export interface AirFreightDetails {
  flights?: number;
  weight?: number;
  routeLegs?: RouteLegs[];
}

export interface Portfolio {
  type: string;
  impact: string;
  currency: string;
  childPortfolios: VirtualPortfolio[];
}
export interface OrderItem {
  type: string;
  portfolioId: string | null;
  portfolio?: Portfolio;
  impact: string;
  price?: number;
  vat?: number;
  tax?: number;
  kilosCo2?: number;
  details?: {
    person?: PersonDetails;
    flight?: FlightDetails;
    shipping?: ShippingDetails;
    event?: EventDetails;
  };
  feeFactors?: {
    flightPax?: number;
    eventFlightPax?: number;
    eventPersons?: number;
    bulkCo2?: boolean;
    percentageBulkCo2?: boolean;
    shipping?: boolean;
    bulkShipping?: boolean;
    shippingPercentage?: boolean;
    airFreight?: boolean;
    flightDistanceKm?: number;
    flight?: boolean;
  };
  taxFactors?: {
    airports?: string[];
    customerCountry?: string;
    customerCountrySubdivision?: string;
  };
}

export interface Metadata {
  flight?: {
    fromAirport?: string;
    toAirport?: string;
    fromCity?: string;
    toCity?: string;
    fromCountry?: string;
    toCountry?: string;
    travelersCount: number;
    distanceKm?: number;
    roundtrip: boolean;
    flights: number;
    passengers: number;
    travelClass: TravelClass;
  };
  order?: {
    channel: string;
  };
  event?: {
    personCount: number;
    country?: string;
    days: number;
    flightLength?: string;
    flightTravelersCount?: number;
    flightRoundTrip?: boolean;
  };
  custom?: {
    internalName: string;
    value: string | Date;
  }[];
  gift?: {
    country: string;
    startDate: string;
    endDate: string;
    plan: string;
  };
}
export interface OrderParams {
  currency: string;
  description?: string;
  comment?: string;
  category: string;
  payment?: {
    type: string;
  };
  items: OrderItem[];
  metadata?: Metadata;
  gift?: boolean;
}

export interface PartnerOrderParams extends OrderParams {
  facilitated: {
    isFacilitated: boolean;
    isPaidByPartner: boolean;
  };
  customer: {
    customerId: string | null;
  };
  costCenter: string;
  poNumber: string;
}
export interface MultiUploadPartnerOrderParams extends OrderParams {
  facilitated: {
    isFacilitated: boolean;
    isPaidByPartner: boolean;
  };
  customer?: {
    customerId: string;
  };
  attachments: { storageBlobName: string; type: string }[];
}

export interface BatchOrderParams extends OrderParams {
  facilitated: {
    isFacilitated: boolean;
    isPaidByPartner: boolean;
  };
  customer?: {
    customerId: string | null;
  };
  costCenter?: string;
  poNumber?: string;
  attachments?: { storageBlobName: string; type: string }[];
}

interface OrderResponse {
  orderId: string;
  order: Order;
  stripePaymentIntentId?: string;
  stripeClientSecret?: string;
  stripePaymentMethodId?: string;
  claimCode?: string;
  inviteId?: string;
  billId?: string;
  fileUrl?: string;
}

export interface BatchOrderResponse {
  orderIds: string[];
  orders: Partial<OrderResponse[]>;
  order: OrderResponse;
  attachment?: {
    storageBlobName: string;
    type: number;
  };
  fileUrl?: string;
}

export interface PlaceOrderSummary {
  facilitatedBatchOrderResult: BatchOrderResponse;
}

export interface ApproveParamsItem {
  portfolioId?: string;
  price: number;
  kilosCo2?: number;
  virtualPortfolios?: {
    weight: number;
    portfolioId: string;
  }[];
}

export interface AdditionalApproveParams {
  costCenter?: string;
  poNumber?: string;
}

export interface ApproveParams extends AdditionalApproveParams {
  currency: string;
  items: ApproveParamsItem[];
}

export type SortKey =
  | 'userName' // These are currently unavailable due to the way order
  | 'userName_desc' // database is structured. We'll see after the MVP
  | 'createdDate'
  | 'createdDate_desc'
  | 'orderType'
  | 'orderType_desc'
  | 'orderName'
  | 'orderName_desc';

interface GetAllOrdersRequest extends PaginationParams {
  category?: string;
  fromCreatedDate?: string;
  toCreatedDate?: string;
  'metadata.custom.department'?: string;
  'metadata.custom.country'?: string;
  sortKey?: SortKey;
  'payment.status'?: string;
}

interface GetAllFacilitatedOrdersRequest extends PaginationParams {
  isfacilitated: boolean;
  excludeMulticustomer: boolean;
}

interface GetAllOrdersResponse<T> {
  totalCount: number;
  offset: number;
  limit: number;
  count: number;
  items: T[];
}

export const create = (instance: AxiosInstance) => async (order: OrderParams, customerId: string): Promise<OrderResponse> => {
  const { data } = await instance.post<OrderResponse>(`/customers/${customerId}/orders`, order);
  return data;
};

export const createForPartner = (instance: AxiosInstance) => async (order: PartnerOrderParams, partnershipId: string): Promise<OrderResponse> => {
  const { data } = await instance.post<OrderResponse>(`/partnerships/${partnershipId}/orders`, order);
  return data;
};

export const createBatchOrderJobForPartner = (instance: AxiosInstance) => async (
  order: BatchOrderParams,
  partnershipId: string,
): Promise<BatchJobResponseOk & BatchJobResponseError> => {
  const { data } = await instance.post<BatchJobResponseOk & BatchJobResponseError>(`/partnerships/${partnershipId}/orders/batchjob`, order);
  return data;
};

export const getAll = (instance: AxiosInstance) => async (params: GetAllOrdersRequest, customerId: string): Promise<GetAllOrdersResponse<Order>> => {
  const { data } = await instance.get<GetAllOrdersResponse<Order>>(`customers/${customerId}/orders`, {
    params,
  });

  return data;
};

interface OrderExpandParams {
  includeVirtualPortfolio?: boolean;
  includeStats?: boolean;
  includeAttachments?: boolean;
}

export const getOne = (instance: AxiosInstance) => async (
  orderId: string,
  customerId: string,
  expandParamsFlags?: OrderExpandParams,
): Promise<Order> => {
  const expandParams: string[] = [];

  if (expandParamsFlags?.includeStats) {
    expandParams.push('facilitated.statistics');
  }
  if (expandParamsFlags?.includeVirtualPortfolio) {
    expandParams.push('virtualportfolio');
  }
  if (expandParamsFlags?.includeAttachments) {
    expandParams.push('attachments');
  }

  const expand = expandParams.join(',');

  const { data } = await instance.get<Order>(`customers/${customerId}/orders/${orderId}`, {
    params: { expand, includeVirtualPortfolio: expandParamsFlags?.includeVirtualPortfolio },
  });

  return data;
};

export const getFootprint = (instance: AxiosInstance) => async (orderId: string, customerId: string): Promise<Footprint[]> => {
  const { data } = await instance.get<Footprint[]>(`customers/${customerId}/orders/${orderId}/footprints`);
  return data;
};

const approveOrder = (instance: AxiosInstance) => async (
  orderId: string,
  customerId: string,
  approveParams?: ApproveParams | {},
): Promise<OrderResponse> => {
  const { data } = await instance.post<OrderResponse>(`customers/${customerId}/orders/${orderId}/approve`, approveParams);
  return data;
};

const rejectOrder = (instance: AxiosInstance) => async (orderId: string, customerId: string): Promise<void> => {
  await instance.post(`customers/${customerId}/orders/${orderId}/reject`);
};

export const getQuoteForPartner = (instance: AxiosInstance) => async (order: OrderParams, partnershipId: string): Promise<Order> => {
  const { data } = await instance.post<Order>(`/partnerships/${partnershipId}/quote`, order);
  return data;
};

export const getQuoteForCustomer = (instance: AxiosInstance) => async (order: OrderParams, customerId: string): Promise<Order> => {
  const { data } = await instance.post<Order>(`/customers/${customerId}/quote`, order);
  return data;
};

export const getFacilitatedOrdersForSingleCustomers = (instance: AxiosInstance) => async (
  params: Partial<GetAllFacilitatedOrdersRequest>,
  partnershipId: string,
): Promise<GetAllOrdersResponse<Order>> => {
  const { data } = await instance.get<GetAllOrdersResponse<Order>>(`/partnerships/${partnershipId}/orders`, {
    params: {
      ...params,
      isfacilitated: true,
      excludeMulticustomer: true,
    },
  });

  return data;
};

export const getFacilitatedOrdersForMultipleCustomers = (instance: AxiosInstance) => async (
  params: Partial<GetAllFacilitatedOrdersRequest>,
  partnershipId: string,
): Promise<GetAllOrdersResponse<MultiCustomersOrder>> => {
  const { data } = await instance.get(`/partnerships/${partnershipId}/facilitatedordersummaries`, {
    params: {
      ...params,
    },
  });

  return data;
};

const handlers = {
  create,
  getAll,
  getOne,
  getFootprint,
  createForPartner,
  approveOrder,
  rejectOrder,
  getQuoteForPartner,
  getQuoteForCustomer,
  createBatchOrderJobForPartner,
  getFacilitatedOrdersForSingleCustomers,
  getFacilitatedOrdersForMultipleCustomers,
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const createOrderApi = (context: PortalContext) => {
  return createContextAwareHandlers(context, handlers);
};

export default createOrderApi;
